import { webappClient } from "../clients";
import { validatePayload, validateResponse } from "../validation";
import { z } from "zod";
import {
  SUPPORTED_CLAIM_TYPES,
  SUPPORTED_ELIGIBILITY_RESULTS,
  SUPPORTED_POTENTIAL_REGULATIONS,
} from "../../settings/constants";

const fetchFlightDataResponseSchema = z.object({
  potential_regulation: z.enum(SUPPORTED_POTENTIAL_REGULATIONS).nullish(),
  eligibility_check_result: z
    .object({
      regulation: z.string().nullish(),
      final_eligibility: z.enum(SUPPORTED_ELIGIBILITY_RESULTS).nullish(),
      disruption_reason: z.enum(SUPPORTED_CLAIM_TYPES).nullish(),
      potential_compensation: z.number().nullish(),
      compensation_in_currencies: z
        .object({
          EUR: z.number(),
          USD: z.number(),
          BRL: z.number().optional(),
          GBP: z.number().optional(),
          CAD: z.number().optional(),
        })
        .nullish(),
      rejection_reason: z.string().nullish(),
      standard: z.boolean(),
    })
    .nullish(),
  itinerary: z.object({
    flights: z.array(
      z.object({
        arrival_airport: z.object({
          iata: z.string(),
          city: z.string(),
          name: z.string(),
        }),
        departure_airport: z.object({
          iata: z.string(),
          city: z.string(),
          name: z.string(),
        }),
        airline: z
          .object({
            iata: z.string(),
            name: z.string(),
            unique_identifier: z.string(),
          })
          .nullish(),
        flight_identifier: z.string().nullish(),
        flight_number: z.string(),
        delay_mins: z.number().nullish(),
        distance_km: z.number().nullish(),
        local_departure_date: z.string(),
        arrival_time: z.string().nullish(),
        actual_arrival_time: z.string().nullish(),
        departure_time: z.string().nullish(),
        actual_departure_time: z.string().nullish(),
        recognized: z.boolean(),
        informed: z.number().nullish(),
        disrupted: z.boolean().nullish(),
      }),
    ),
  }),
});

export type RawBarcodeLeadsFlights = z.infer<typeof fetchFlightDataResponseSchema>;

const fetchFlightDataPayloadSchema = z.object({
  flights: z.array(z.string()),
  token: z.string(),
});

export async function fetchFlightData(flightsIdentifiers: (string | null)[], token?: string) {
  const validatedPayload = validatePayload({
    data: { flights: flightsIdentifiers, token: token || "" },
    schema: fetchFlightDataPayloadSchema,
    tag: `barcode leads API, ${flightsIdentifiers}`,
  });

  const data = await webappClient
    .post("api/barcode_leads", {
      json: validatedPayload,
    })
    .json();

  return validateResponse({
    data,
    schema: fetchFlightDataResponseSchema,
    tag: `barcode leads API, ${flightsIdentifiers}`,
  });
}
